import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Key,
  Buildings,
  Cloud,
  ClockCounterClockwise,
  LockKey,
  PlugsConnected,
  Plus,
  ShieldCheck,
  SignOut,
  UserCircle,
  UsersThree,
} from "@phosphor-icons/react";
import { useAuth } from "../../app/auth";
import { Modal, StatusTag } from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { useToast } from "../../components/Toast";
import { formatDateTime, formatRole } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import type {
  Account,
  AuthSession,
  IntegrationApiKey,
  IntegrationWebhook,
  IntegrationWebhookDelivery,
  RoleTemplate,
  TenantSettings,
} from "../../lib/types";

const permissionLabels: Record<string, string> = {
  "accounts:manage": "成员与角色管理",
  "sessions:manage": "会话管理",
  "approvals:review": "任务审批",
  "bindings:create": "创建设备绑定",
  "devices:read": "读取设备",
  "devices:manage": "管理设备",
  "tasks:read": "读取任务",
  "tasks:create": "创建任务",
  "tasks:manage": "管理任务",
  "alerts:read": "读取告警",
  "alerts:manage": "处置告警",
  "alert-rules:manage": "管理告警规则",
  "archives:read": "读取归档",
  "audit:read": "读取审计",
  "audit:export": "导出审计",
  "tenant:read": "读取企业设置",
  "tenant:manage": "管理企业设置",
  "policies:read": "读取策略",
  "policies:manage": "管理策略",
  "apps:read": "读取应用",
  "apps:manage": "管理应用",
  "reports:read": "读取报表",
  "reports:export": "导出报表",
  "integrations:read": "读取开放集成",
  "integrations:manage": "管理开放集成",
};

const apiKeyScopeOptions = ["devices:read", "tasks:read", "alerts:read", "audit:read", "reports:read"];
const webhookEventOptions = ["task.created", "task.completed", "task.failed", "alert.created", "alert.acknowledged", "alert.resolved", "device.online", "device.offline"];

function AccountModal({ account, onClose }: { account?: Account; onClose(): void }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [username, setUsername] = useState(account?.username || "");
  const [password, setPassword] = useState("");
  const [roleCode, setRoleCode] = useState(account?.roleCode || "operator");
  const [status, setStatus] = useState(account?.status || "enabled");
  const mutation = useMutation({
    mutationFn: () =>
      account
        ? request(`/api/accounts/${account.id}`, {
            method: "PATCH",
            body: JSON.stringify({ roleCode, status }),
          })
        : request("/api/accounts", {
            method: "POST",
            body: JSON.stringify({ username, password, roleCode }),
          }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      notify(account ? "成员信息已更新" : "成员已创建", username);
      onClose();
    },
  });
  return (
    <Modal
      title={account ? "编辑成员" : "添加成员"}
      description={account ? `账号 #${account.id}` : "创建新的企业管理账号"}
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button compact" onClick={onClose}>取消</button>
          <button
            className="primary-button"
            disabled={mutation.isPending || (!account && (!username.trim() || password.length < 8))}
            onClick={() => mutation.mutate()}
          >{mutation.isPending ? "正在保存…" : "保存"}</button>
        </>
      }
    >
      <div className="form-stack">
        {!account && (
          <>
            <label><span>登录账号</span><input type="text" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="例如 operator01" /></label>
            <label><span>初始密码</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="至少 8 位字符" /></label>
          </>
        )}
        <label><span>角色</span><select value={roleCode} onChange={(event) => setRoleCode(event.target.value)}><option value="tenant_admin">租户管理员</option><option value="operator">运营人员</option><option value="auditor">审计员</option></select></label>
        {account && <label><span>账号状态</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="enabled">启用</option><option value="disabled">禁用</option></select></label>}
        {mutation.isError && <p className="form-error">保存失败，账号名可能已存在或当前操作不被允许。</p>}
      </div>
    </Modal>
  );
}

function PasswordModal({ account, onClose }: { account?: Account; onClose(): void }) {
  const { request } = useAuth();
  const notify = useToast();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      account
        ? request(`/api/accounts/${account.id}/reset-password`, {
            method: "POST",
            body: JSON.stringify({ newPassword }),
          })
        : request("/api/auth/change-password", {
            method: "POST",
            body: JSON.stringify({ oldPassword, newPassword }),
          }),
    onSuccess: () => {
      notify(account ? "成员密码已重置" : "密码已修改", account?.username);
      onClose();
    },
  });
  return (
    <Modal title={account ? `重置 ${account.username} 的密码` : "修改我的密码"} onClose={onClose}
      footer={<><button className="secondary-button compact" onClick={onClose}>取消</button><button className="primary-button" disabled={mutation.isPending || newPassword.length < 8 || (!account && !oldPassword)} onClick={() => mutation.mutate()}>{mutation.isPending ? "正在提交…" : "确认修改"}</button></>}>
      <div className="form-stack">
        {!account && <label><span>当前密码</span><input type="password" value={oldPassword} onChange={(event) => setOldPassword(event.target.value)} /></label>}
        <label><span>新密码</span><input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="至少 8 位字符" /></label>
        <p className="operation-note">密码修改后，相关账号的其他会话将被撤销。</p>
        {mutation.isError && <p className="form-error">密码修改失败，请检查当前密码和新密码强度。</p>}
      </div>
    </Modal>
  );
}

function MembersPanel() {
  const accountsQuery = useApiQuery<Account[]>(["accounts"], "/api/accounts");
  const [editing, setEditing] = useState<Account | "new" | null>(null);
  const [resetting, setResetting] = useState<Account | null>(null);
  if (accountsQuery.isError) return <QueryErrorState onRetry={() => void accountsQuery.refetch()} />;
  return (
    <section className="settings-card">
      <header><div><h2>企业成员</h2><p>管理登录账号、角色与启用状态</p></div><button className="primary-button" onClick={() => setEditing("new")}><Plus size={16} />添加成员</button></header>
      <div className="member-list">
        {(accountsQuery.data || []).map((account) => (
          <div key={account.id}>
            <span className="member-avatar"><UserCircle size={29} /></span>
            <span><strong>{account.username}</strong><small>{formatRole(account.roleCode)} · 创建于 {formatDateTime(account.createdAt)}</small></span>
            <StatusTag value={account.status} />
            <button className="text-button" onClick={() => setResetting(account)}><Key size={15} />重置密码</button>
            <button className="secondary-button compact" onClick={() => setEditing(account)}>编辑</button>
          </div>
        ))}
        {accountsQuery.isPending && <div className="table-empty">正在读取成员…</div>}
      </div>
      {editing && <AccountModal account={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} />}
      {resetting && <PasswordModal account={resetting} onClose={() => setResetting(null)} />}
    </section>
  );
}

function RolesPanel() {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const rolesQuery = useApiQuery<RoleTemplate[]>(["roles"], "/api/roles");
  const accountsQuery = useApiQuery<Account[]>(["accounts"], "/api/accounts");
  const [editing, setEditing] = useState<RoleTemplate | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const beginEdit = (role: RoleTemplate) => {
    setEditing(role);
    setPermissions(role.effectivePermissions);
  };
  const save = useMutation({
    mutationFn: () =>
      request(`/api/roles/${editing?.roleCode}`, {
        method: "PATCH",
        body: JSON.stringify({ permissions }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      notify("角色权限已更新", editing ? formatRole(editing.roleCode) : undefined);
      setEditing(null);
    },
  });
  if (rolesQuery.isError) return <QueryErrorState onRetry={() => void rolesQuery.refetch()} />;
  const addedPermissions = editing ? permissions.filter((permission) => !editing.effectivePermissions.includes(permission)) : [];
  const removedPermissions = editing ? editing.effectivePermissions.filter((permission) => !permissions.includes(permission)) : [];
  const impactedAccounts = editing ? (accountsQuery.data || []).filter((account) => account.roleCode === editing.roleCode && !account.permissions).length : 0;
  return (
    <section className="settings-card">
      <header><div><h2>角色权限</h2><p>角色模板会应用于未设置账号级权限的成员</p></div></header>
      <div className="role-grid">
        {(rolesQuery.data || []).map((role) => (
          <article key={role.roleCode}>
            <ShieldCheck size={24} />
            <h3>{formatRole(role.roleCode)}</h3>
            <p>{role.effectivePermissions.length} 项有效权限 · {role.usesSystemDefault ? "系统默认" : "租户自定义"}</p>
            <div>{role.effectivePermissions.slice(0, 5).map((permission) => <span key={permission}>{permissionLabels[permission] || permission}</span>)}</div>
            <button className="secondary-button compact" onClick={() => beginEdit(role)}>配置权限</button>
          </article>
        ))}
      </div>
      {editing && (
        <Modal title={`配置${formatRole(editing.roleCode)}`} description="保存后会影响使用该角色模板的账号" onClose={() => setEditing(null)}
          footer={<><button className="secondary-button compact" onClick={() => setEditing(null)}>取消</button><button className="primary-button" disabled={save.isPending} onClick={() => save.mutate()}>保存权限</button></>}>
          <div className="permission-grid">
            {Object.entries(permissionLabels).map(([permission, label]) => (
              <label key={permission}><input type="checkbox" checked={permissions.includes(permission)} onChange={() => setPermissions((current) => current.includes(permission) ? current.filter((item) => item !== permission) : [...current, permission])} /><span><strong>{label}</strong><small>{permission}</small></span></label>
            ))}
          </div>
          <section className="permission-impact"><strong>变更影响预览</strong><p>将影响 {impactedAccounts} 个继承“{formatRole(editing.roleCode)}”模板的账号。</p><div><span className="added">新增 {addedPermissions.length} 项</span><span className="removed">移除 {removedPermissions.length} 项</span></div>{removedPermissions.length > 0 && <small>移除：{removedPermissions.map((permission) => permissionLabels[permission] || permission).join("、")}</small>}</section>
          {save.isError && <p className="form-error">权限保存失败。</p>}
        </Modal>
      )}
    </section>
  );
}

function SessionsPanel() {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const sessionsQuery = useApiQuery<AuthSession[]>(["sessions"], "/api/auth/sessions");
  const revoke = useMutation({
    mutationFn: (id: string) => request(`/api/auth/sessions/${id}/revoke`, { method: "POST" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sessions"] });
      notify("会话已撤销");
    },
  });
  if (sessionsQuery.isError) return <QueryErrorState onRetry={() => void sessionsQuery.refetch()} />;
  return (
    <section className="settings-card">
      <header><div><h2>登录会话</h2><p>查看企业账号会话并强制下线</p></div></header>
      <div className="session-list">
        {(sessionsQuery.data || []).map((session) => (
          <div key={session.id}>
            <span className="session-icon"><LockKey size={21} /></span>
            <span><strong>{session.username}</strong><small>{session.sessionKey} · 最近使用 {formatDateTime(session.lastUsedAt)}</small></span>
            <StatusTag value={session.active ? "active" : "revoked"} />
            <span className="session-expiry">刷新凭证至<br />{formatDateTime(session.refreshExpiresAt)}</span>
            {session.active && <button className="secondary-button compact" disabled={revoke.isPending} onClick={() => revoke.mutate(session.id)}><SignOut size={15} />下线</button>}
          </div>
        ))}
      </div>
    </section>
  );
}

function TenantPanel() {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const settingsQuery = useApiQuery<TenantSettings>(["tenant-settings"], "/api/tenant/settings");
  const [draft, setDraft] = useState<TenantSettings | null>(null);
  const settings = draft || settingsQuery.data;
  const save = useMutation({
    mutationFn: () => request<TenantSettings>("/api/tenant/settings", {
      method: "PATCH",
      body: JSON.stringify(draft),
    }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["tenant-settings"], updated);
      setDraft(null);
      notify("企业设置已保存");
    },
  });
  if (settingsQuery.isError) return <QueryErrorState onRetry={() => void settingsQuery.refetch()} />;
  if (!settings) return <section className="settings-card"><div className="table-empty">正在读取企业设置…</div></section>;
  const update = <K extends keyof TenantSettings>(key: K, value: TenantSettings[K]) =>
    setDraft({ ...settings, [key]: value });
  return (
    <section className="settings-card">
      <header><div><h2>企业与数据设置</h2><p>明确配置租户、通知、保留周期和对象存储</p></div><button className="primary-button" disabled={!draft || save.isPending} onClick={() => save.mutate()}>{save.isPending ? "正在保存…" : "保存设置"}</button></header>
      <div className="settings-form">
        <div className="settings-section"><h3>企业信息</h3><div className="form-columns"><label><span>企业名称</span><input value={settings.name} onChange={(event) => update("name", event.target.value)} /></label><label><span>租户标识</span><input value={settings.tenantGuid} disabled /></label></div><label><span>时区</span><select value={settings.timezone} onChange={(event) => update("timezone", event.target.value)}><option value="Asia/Shanghai">Asia/Shanghai</option><option value="UTC">UTC</option></select></label></div>
        <div className="settings-section"><h3>通知与告警</h3><div className="form-columns"><label><span>通知邮箱</span><input value={settings.notificationEmail || ""} onChange={(event) => update("notificationEmail", event.target.value)} placeholder="ops@example.com" /></label><label><span>离线告警阈值（分钟）</span><input type="number" min={1} max={1440} value={settings.offlineAlertMinutes} onChange={(event) => update("offlineAlertMinutes", Number(event.target.value))} /></label></div></div>
        <div className="settings-section"><h3>数据与存储</h3><div className="form-columns"><label><span>数据保留天数</span><input type="number" min={7} max={3650} value={settings.retentionDays} onChange={(event) => update("retentionDays", Number(event.target.value))} /></label><label><span>存储提供方</span><select value={settings.storageProvider} onChange={(event) => update("storageProvider", event.target.value)}><option value="local">本地存储</option><option value="s3">S3 兼容存储</option><option value="oss">阿里云 OSS</option></select></label></div><label><span>存储桶</span><input value={settings.storageBucket || ""} onChange={(event) => update("storageBucket", event.target.value)} placeholder="仅保存名称，不返回访问密钥" /></label></div>
        {save.isError && <p className="form-error">设置保存失败，请检查数值范围。</p>}
      </div>
    </section>
  );
}

function WebhookModal({ onClose }: { onClose(): void }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [eventTypes, setEventTypes] = useState(["task.completed", "alert.created"]);
  const [maxRetries, setMaxRetries] = useState(3);
  const [timeoutSeconds, setTimeoutSeconds] = useState(10);
  const mutation = useMutation({
    mutationFn: () => request<{ secret: string }>("/api/integrations/webhooks", {
      method: "POST",
      body: JSON.stringify({ name, url, eventTypes, maxRetries, timeoutSeconds }),
    }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["integration-webhooks"] });
      setSecret(created.secret);
      notify("Webhook 已创建");
    },
  });
  return (
    <Modal title={secret ? "保存 Webhook 密钥" : "创建 Webhook"} description={secret ? "该密钥只显示一次，请立即保存到安全位置。" : "仅接受 HTTPS 地址，签名密钥创建后不可再次查看。"} onClose={onClose}
      footer={secret ? <button className="primary-button" onClick={onClose}>我已安全保存</button> : <><button className="secondary-button compact" onClick={onClose}>取消</button><button className="primary-button" disabled={!name.trim() || !url.startsWith("https://") || !eventTypes.length || mutation.isPending} onClick={() => mutation.mutate()}>创建</button></>}>
      {secret ? <div className="secret-reveal"><Key size={26} /><strong>Webhook 签名密钥</strong><code>{secret}</code><p>关闭后系统只保留 hash 和末 6 位，无法恢复明文。</p></div> : <div className="form-stack"><label><span>名称</span><input type="text" value={name} onChange={(event) => setName(event.target.value)} /></label><label><span>HTTPS 接收地址</span><input type="text" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://..." /></label><div><span className="field-caption">订阅事件</span><div className="event-grid">{webhookEventOptions.map((eventType) => <label key={eventType}><input type="checkbox" checked={eventTypes.includes(eventType)} onChange={() => setEventTypes((current) => current.includes(eventType) ? current.filter((item) => item !== eventType) : [...current, eventType])} /><span>{eventType}</span></label>)}</div></div><div className="form-columns"><label><span>失败重试次数</span><input type="number" min={0} max={5} value={maxRetries} onChange={(event) => setMaxRetries(Number(event.target.value))} /></label><label><span>超时（秒）</span><input type="number" min={3} max={30} value={timeoutSeconds} onChange={(event) => setTimeoutSeconds(Number(event.target.value))} /></label></div>{mutation.isError && <p className="form-error">Webhook 创建失败，请检查地址、事件与重试设置。</p>}</div>}
    </Modal>
  );
}

function WebhookDeliveriesModal({ webhook, onClose }: { webhook: IntegrationWebhook; onClose(): void }) {
  const deliveriesQuery = useApiQuery<IntegrationWebhookDelivery[]>(["integration-webhook-deliveries", webhook.id], `/api/integrations/webhooks/${webhook.id}/deliveries`);
  return <Modal title="Webhook 投递记录" description={`${webhook.name} · 最近 30 次尝试`} onClose={onClose} footer={<button className="primary-button" onClick={onClose}>关闭</button>}><div className="delivery-list">{(deliveriesQuery.data || []).map((delivery) => <div key={delivery.id}><ClockCounterClockwise size={18} /><span><strong>{delivery.eventType} · 第 {delivery.attempt} 次</strong><small>{formatDateTime(delivery.createdAt)} · {delivery.durationMs ?? "—"} ms{delivery.errorMessage ? ` · ${delivery.errorMessage}` : ""}</small></span><StatusTag value={delivery.status} tone={delivery.status === "succeeded" ? "green" : "red"} /><em>{delivery.responseStatus || "—"}</em></div>)}{deliveriesQuery.isPending && <p className="inline-empty">正在读取投递记录…</p>}{!deliveriesQuery.isPending && !deliveriesQuery.data?.length && <p className="inline-empty">尚无投递记录，请先执行连通性测试。</p>}</div></Modal>;
}

function ApiKeyModal({ rotating, onClose }: { rotating?: IntegrationApiKey; onClose(): void }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [name, setName] = useState(rotating?.name || "");
  const [key, setKey] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [scopes, setScopes] = useState<string[]>(rotating?.scopes || ["devices:read"]);
  const mutation = useMutation({
    mutationFn: () => request<{ key: string }>(rotating ? `/api/integrations/api-keys/${rotating.id}/rotate` : "/api/integrations/api-keys", { method: "POST", body: rotating ? undefined : JSON.stringify({ name, scopes, expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined }) }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["integration-api-keys"] });
      setKey(created.key);
      notify(rotating ? "API 密钥已轮换" : "API 密钥已创建");
    },
  });
  return (
    <Modal title={key ? "保存 API 密钥" : rotating ? "轮换 API 密钥" : "创建 API 密钥"} description={key ? "该密钥只显示一次，请立即保存到密钥管理器。" : rotating ? "旧密钥会立即撤销，新密钥继承作用域与到期时间。" : "只授予集成必需的最小读取权限。"} onClose={onClose}
      footer={key ? <button className="primary-button" onClick={onClose}>我已安全保存</button> : <><button className="secondary-button compact" onClick={onClose}>取消</button><button className="primary-button" disabled={!name.trim() || !scopes.length || mutation.isPending} onClick={() => mutation.mutate()}>{rotating ? "确认轮换" : "创建"}</button></>}>
      {key ? <div className="secret-reveal"><Key size={26} /><strong>API 密钥</strong><code>{key}</code><p>关闭后无法再次读取；轮换后旧密钥已经失效。</p></div> : <div className="form-stack"><label><span>密钥名称</span><input type="text" value={name} disabled={Boolean(rotating)} onChange={(event) => setName(event.target.value)} placeholder="例如 BI 报表服务" /></label>{!rotating && <><label><span>到期日期（可选）</span><input type="date" min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)} value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} /></label><div><span className="field-caption">最小权限作用域</span><div className="event-grid">{apiKeyScopeOptions.map((scope) => <label key={scope}><input type="checkbox" checked={scopes.includes(scope)} onChange={() => setScopes((current) => current.includes(scope) ? current.filter((item) => item !== scope) : [...current, scope])} /><span>{permissionLabels[scope] || scope}<small>{scope}</small></span></label>)}</div></div></>}{mutation.isError && <p className="form-error">密钥操作失败，请检查作用域与到期时间。</p>}</div>}
    </Modal>
  );
}

function IntegrationsPanel() {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const webhooksQuery = useApiQuery<IntegrationWebhook[]>(["integration-webhooks"], "/api/integrations/webhooks");
  const keysQuery = useApiQuery<IntegrationApiKey[]>(["integration-api-keys"], "/api/integrations/api-keys");
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [keyOpen, setKeyOpen] = useState(false);
  const [rotatingKey, setRotatingKey] = useState<IntegrationApiKey | null>(null);
  const [deliveryWebhook, setDeliveryWebhook] = useState<IntegrationWebhook | null>(null);
  const testWebhook = useMutation({
    mutationFn: (id: string) => request<{ ok: boolean; status?: number; attempts: number }>(`/api/integrations/webhooks/${id}/test`, { method: "POST" }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["integration-webhooks"] });
      await queryClient.invalidateQueries({ queryKey: ["integration-webhook-deliveries"] });
      notify(result.ok ? "Webhook 连通性测试成功" : "Webhook 连通性测试失败", `${result.attempts} 次尝试${result.status ? ` · HTTP ${result.status}` : ""}`);
    },
  });
  const deleteWebhook = useMutation({
    mutationFn: (id: string) => request(`/api/integrations/webhooks/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["integration-webhooks"] });
      notify("Webhook 已删除");
    },
  });
  const revokeKey = useMutation({
    mutationFn: (id: string) => request(`/api/integrations/api-keys/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["integration-api-keys"] });
      notify("API 密钥已撤销");
    },
  });
  if (webhooksQuery.isError || keysQuery.isError) return <QueryErrorState onRetry={() => { void webhooksQuery.refetch(); void keysQuery.refetch(); }} />;
  return (
    <section className="settings-card">
      <header><div><h2>开放集成</h2><p>Webhook 与 API 密钥均按租户隔离并记录审计</p></div></header>
      <div className="integration-section"><header><div><PlugsConnected size={20} /><span><strong>Webhook</strong><small>可选择事件，并记录带重试的连通性测试</small></span></div><button className="secondary-button compact" onClick={() => setWebhookOpen(true)}><Plus size={15} />添加</button></header><div className="integration-list">{(webhooksQuery.data || []).map((webhook) => <div key={webhook.id}><span><strong>{webhook.name}</strong><small>{webhook.url} · {webhook.eventTypes.length} 类事件 · 重试 {webhook.maxRetries} 次 · 最近测试 {formatDateTime(webhook.lastTestedAt)}</small></span><StatusTag value={webhook.status} /><button className="text-button" disabled={testWebhook.isPending} onClick={() => testWebhook.mutate(webhook.id)}>测试</button><button className="text-button" onClick={() => setDeliveryWebhook(webhook)}>记录</button><button className="text-button danger-text" onClick={() => deleteWebhook.mutate(webhook.id)}>删除</button></div>)}{!webhooksQuery.data?.length && <p className="inline-empty">尚未配置 Webhook。</p>}</div></div>
      <div className="integration-section"><header><div><Key size={20} /><span><strong>API 密钥</strong><small>支持 Bearer 鉴权、最小作用域、到期和轮换</small></span></div><button className="secondary-button compact" onClick={() => setKeyOpen(true)}><Plus size={15} />创建</button></header><div className="integration-list">{(keysQuery.data || []).map((keyItem) => <div key={keyItem.id}><span><strong>{keyItem.name}</strong><small>末尾 {keyItem.keySuffix} · {keyItem.scopes.length} 项作用域 · 到期 {formatDateTime(keyItem.expiresAt)} · 最近使用 {formatDateTime(keyItem.lastUsedAt)}</small></span><StatusTag value={keyItem.status} /><button className="text-button" disabled={keyItem.status === "revoked"} onClick={() => setRotatingKey(keyItem)}>轮换</button><button className="text-button danger-text" disabled={keyItem.status === "revoked"} onClick={() => revokeKey.mutate(keyItem.id)}>撤销</button></div>)}{!keysQuery.data?.length && <p className="inline-empty">尚未创建 API 密钥。</p>}</div></div>
      {webhookOpen && <WebhookModal onClose={() => setWebhookOpen(false)} />}
      {keyOpen && <ApiKeyModal onClose={() => setKeyOpen(false)} />}
      {rotatingKey && <ApiKeyModal rotating={rotatingKey} onClose={() => setRotatingKey(null)} />}
      {deliveryWebhook && <WebhookDeliveriesModal webhook={deliveryWebhook} onClose={() => setDeliveryWebhook(null)} />}
    </section>
  );
}

export function SettingsPage() {
  const [tab, setTab] = useState<"tenant" | "members" | "roles" | "sessions" | "integrations">("tenant");
  const [passwordOpen, setPasswordOpen] = useState(false);
  return (
    <div className="settings-layout">
      <aside className="settings-nav">
        <button className={tab === "tenant" ? "active" : ""} onClick={() => setTab("tenant")}><Buildings size={18} /><span><strong>企业设置</strong><small>通知、数据与存储</small></span></button>
        <button className={tab === "members" ? "active" : ""} onClick={() => setTab("members")}><UsersThree size={18} /><span><strong>成员管理</strong><small>账号与状态</small></span></button>
        <button className={tab === "roles" ? "active" : ""} onClick={() => setTab("roles")}><ShieldCheck size={18} /><span><strong>角色权限</strong><small>权限模板</small></span></button>
        <button className={tab === "sessions" ? "active" : ""} onClick={() => setTab("sessions")}><LockKey size={18} /><span><strong>会话管理</strong><small>查看与下线</small></span></button>
        <button className={tab === "integrations" ? "active" : ""} onClick={() => setTab("integrations")}><Cloud size={18} /><span><strong>开放集成</strong><small>Webhook 与 API 密钥</small></span></button>
        <button onClick={() => setPasswordOpen(true)}><Key size={18} /><span><strong>修改密码</strong><small>当前登录账号</small></span></button>
      </aside>
      <div>{tab === "tenant" ? <TenantPanel /> : tab === "members" ? <MembersPanel /> : tab === "roles" ? <RolesPanel /> : tab === "sessions" ? <SessionsPanel /> : <IntegrationsPanel />}</div>
      {passwordOpen && <PasswordModal onClose={() => setPasswordOpen(false)} />}
    </div>
  );
}
